/**
 * Entry Detail Route
 * Full-page view for a single entry with edit/delete. The read-only detail body
 * is shared with the inline expanded card via EntryDetailBody.
 */

import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useState, useCallback } from 'react'
import { FullPage } from '@/components/common/FullPage'
import { ConfirmDialog } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Spinner } from '@/components/common/Spinner'
import { EntryForm } from '@/components/features/entries/EntryForm'
import { EntryDetailBody } from '@/components/features/entries/EntryDetail/EntryDetailBody'
import { useEntry, useDeleteEntry } from '@/hooks'
import { useHasPermission } from '@/hooks/usePermissions'
import { useToast } from '@/providers/ToastProvider'
import { useAuth } from '@/providers/AuthProvider'
import { useMyTeamIds, canEditEntryFor, canDeleteEntryFor } from '@/hooks/useCanEditEntry'
import { haptics } from '@/lib/haptics'

export const Route = createFileRoute('/_authenticated/entries_/$entryId')({
  component: EntryDetailRoute,
  // Allow deep-linking straight into edit mode (e.g. from the inline card's Edit action).
  validateSearch: (search: Record<string, unknown>): { edit?: boolean } => ({
    edit: search.edit === true || search.edit === 'true',
  }),
})

function EntryDetailRoute() {
  const { entryId } = Route.useParams()
  const { edit } = Route.useSearch()
  const router = useRouter()
  const { data: entry, isLoading, error } = useEntry(entryId)
  const deleteEntry = useDeleteEntry()
  const { showToast } = useToast()
  // Read-only users (e.g. View Only) can view an entry but not edit/delete it —
  // and can't deep-link into edit mode via ?edit=true either (#648).
  //
  // Who it belongs to is the second half of that (#1373, #1379). Edit: the
  // owner, the owner's manager, or an admin. Delete: the owner or an admin. A
  // participant can read a shared activity but never change it. These mirror
  // the database policies, which refuse anything else regardless.
  const { user } = useAuth()
  const teamIds = useMyTeamIds()
  const canEditEntry = useHasPermission('entries', 'update') && canEditEntryFor(user, entry, teamIds)
  const canDeleteEntry = useHasPermission('entries', 'delete') && canDeleteEntryFor(user, entry)
  const [isEditing, setIsEditing] = useState(!!edit && canEditEntry)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editFooterEl, setEditFooterEl] = useState<HTMLDivElement | null>(null)
  const editFooterRef = useCallback((node: HTMLDivElement | null) => setEditFooterEl(node), [])

  if (isLoading) {
    return (
      <FullPage title="Activity">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Spinner />
        </div>
      </FullPage>
    )
  }

  if (error || !entry) {
    return (
      <FullPage title="Activity">
        <p style={{ padding: 'var(--space-4)', color: 'var(--text-secondary)' }}>
          Activity not found.
        </p>
      </FullPage>
    )
  }

  if (isEditing && canEditEntry) {
    return (
      <FullPage
        title="Edit Activity"
        onBack={() => setIsEditing(false)}
        footer={<div ref={editFooterRef} />}
      >
        <EntryForm
          entry={entry}
          onSuccess={() => setIsEditing(false)}
          onCancel={() => setIsEditing(false)}
          actionBarContainer={editFooterEl}
        />
      </FullPage>
    )
  }

  const handleDelete = async () => {
    try {
      await deleteEntry.mutateAsync(entry.id)
      haptics.success()
      showToast('Activity deleted', 'success')
      router.history.back()
    } catch {
      haptics.error()
      showToast('Failed to delete activity', 'error')
    }
  }

  const headerActions = canEditEntry ? (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      aria-label="Edit activity"
      style={{
        background: 'none',
        border: 'none',
        color: 'var(--accent-primary)',
        cursor: 'pointer',
        padding: 4,
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
      }}
    >
      Edit
    </button>
  ) : undefined

  return (
    <FullPage title="Activity Details" actions={headerActions}>
      <EntryDetailBody
        entry={entry}
        actions={
          canDeleteEntry ? (
            <Button
              variant="ghost"
              onClick={() => setShowDeleteConfirm(true)}
              style={{ color: 'var(--status-error)', width: '100%' }}
            >
              Delete Activity
            </Button>
          ) : undefined
        }
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Activity"
        message="Are you sure you want to delete this activity? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteEntry.isPending}
      />
    </FullPage>
  )
}
